import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { validateAdminInvite, acceptAdminInvite } from '@/lib/api/invites';

export const Route = createFileRoute('/accept-invite')({
  component: AcceptInvitePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? '',
  }),
});

function PasswordRequirements({ password }: { password: string }) {
  const checks = [
    { label: 'Mínimo 8 caracteres', ok: password.length >= 8 },
    { label: 'Letra maiúscula', ok: /[A-Z]/.test(password) },
    { label: 'Letra minúscula', ok: /[a-z]/.test(password) },
    { label: 'Número', ok: /[0-9]/.test(password) },
  ];
  return (
    <ul className="space-y-1 text-xs">
      {checks.map((c) => (
        <li key={c.label} className={cn(c.ok ? 'text-green-600' : 'text-muted-foreground')}>
          {c.ok ? '✓' : '○'} {c.label}
        </li>
      ))}
    </ul>
  );
}

function allPasswordRequirementsMet(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
}

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteData, setInviteData] = useState<{
    email: string;
    role: string;
    roleLabel: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordsMatch = password === confirmPassword;
  const allRequirementsMet = allPasswordRequirementsMet(password);
  const canSubmit =
    fullName.trim().length >= 3 &&
    allRequirementsMet &&
    passwordsMatch &&
    confirmPassword.length > 0;

  useEffect(() => {
    if (!token) {
      setError('Token de convite não fornecido');
      setLoading(false);
      return;
    }

    validateAdminInvite(token).then((result) => {
      if (!result.valid) {
        setError(result.error || 'Convite inválido ou expirado');
      } else {
        setInviteValid(true);
        setInviteData({
          email: result.email!,
          role: result.role!,
          roleLabel: result.roleLabel!,
        });
      }
      setLoading(false);
    });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (fullName.trim().length < 3) {
      toast.error('Nome deve ter pelo menos 3 caracteres');
      return;
    }
    if (!allRequirementsMet) {
      toast.error('A senha não atende todos os requisitos');
      return;
    }
    if (!passwordsMatch) {
      toast.error('As senhas não coincidem');
      return;
    }

    setSubmitting(true);
    try {
      const result = await acceptAdminInvite({
        token,
        fullName: fullName.trim(),
        password,
      });

      if (!result.success) {
        toast.error(result.error || 'Erro ao criar conta');
        return;
      }

      setSuccess(true);
      toast.success('Conta criada com sucesso!');
      setTimeout(() => navigate({ to: '/login' }), 2000);
    } catch {
      toast.error('Erro ao criar conta. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass = cn(
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
    'ring-offset-background placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
  );

  const btnPrimary = cn(
    'inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground',
    'transition-colors hover:bg-primary/90',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  );

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <p className="text-sm text-muted-foreground">Validando convite...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <div className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm text-center">
          <h1 className="text-2xl font-bold tracking-tight text-green-600">Conta Criada!</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sua conta foi criada com sucesso. Redirecionando para o login...
          </p>
        </div>
      </div>
    );
  }

  if (!inviteValid || error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <div className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm text-center">
          <h1 className="text-2xl font-bold tracking-tight text-destructive">Convite Inválido</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error || 'Este convite não é válido, expirou ou já foi utilizado.'}
          </p>
          <button
            type="button"
            onClick={() => navigate({ to: '/login' })}
            className={cn(btnPrimary, 'mt-6')}
          >
            Ir para o login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold tracking-tight">OuroPix Admin</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Você foi convidado como{' '}
            <span className="font-semibold text-primary">{inviteData?.roleLabel}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={inviteData?.email ?? ''}
              disabled
              className={cn(inputClass, 'bg-muted')}
            />
          </div>

          <div>
            <label htmlFor="fullName" className="mb-1.5 block text-sm font-medium">
              Nome Completo
            </label>
            <input
              id="fullName"
              type="text"
              autoComplete="name"
              required
              autoFocus
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Seu nome completo"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium">
              Senha
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Defina sua senha"
                className={cn(inputClass, 'pr-10')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                tabIndex={-1}
              >
                {showPassword ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            {password.length > 0 && (
              <div className="mt-2">
                <PasswordRequirements password={password} />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="mb-1.5 block text-sm font-medium">
              Confirmar Senha
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a senha"
                className={cn(inputClass, 'pr-10')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                tabIndex={-1}
              >
                {showConfirmPassword ? 'Ocultar' : 'Ver'}
              </button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="mt-1 text-xs text-destructive">As senhas não coincidem</p>
            )}
          </div>

          <button type="submit" disabled={submitting || !canSubmit} className={btnPrimary}>
            {submitting ? 'Criando conta...' : 'Criar Conta'}
          </button>
        </form>
      </div>
    </div>
  );
}
